import React, { useState } from 'react';
import { 
  Plus, 
  Lightbulb, 
  Link2, 
  GitCompare,
  ShoppingCart,
  CreditCard,
  Building2,
  Users,
  Wallet,
  ArrowLeftRight,
  Receipt,
  QrCode,
  TrendingUp,
  Shield,
  FileText,
  Globe,
  Smartphone,
  Zap,
  LucideIcon,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { UseCaseCard } from './UseCaseCard';

interface UseCase {
  id: string;
  title: string;
  description: string;
  businessScenario: string;
  recommendedFor: string[];
  icon?: string;
}

interface ComplementaryTemplate {
  id: string;
  name: string;
  description?: string;
}

interface UseCasesTabProps {
  useCases: UseCase[];
  complementaryTemplates: string[];
  availableTemplates?: ComplementaryTemplate[];
  onChange: (field: string, value: any) => void;
  isDarkMode?: boolean;
}

// Common Lucide icons for use cases
const ICON_MAP: Record<string, LucideIcon> = {
  Lightbulb,
  ShoppingCart,
  CreditCard,
  Building2,
  Users,
  Wallet,
  ArrowLeftRight,
  Receipt,
  QrCode,
  TrendingUp,
  Shield,
  FileText,
  Globe,
  Smartphone,
  Zap,
};

const ICON_OPTIONS = [
  { value: 'Lightbulb', label: 'Ideia' },
  { value: 'ShoppingCart', label: 'E-commerce' },
  { value: 'CreditCard', label: 'Pagamentos' },
  { value: 'Building2', label: 'Empresas' },
  { value: 'Users', label: 'Usuários' },
  { value: 'Wallet', label: 'Carteira' },
  { value: 'ArrowLeftRight', label: 'Transferências' },
  { value: 'Receipt', label: 'Boletos' },
  { value: 'QrCode', label: 'PIX' },
  { value: 'TrendingUp', label: 'Investimentos' },
  { value: 'Shield', label: 'Segurança' },
  { value: 'FileText', label: 'Documentos' },
  { value: 'Globe', label: 'Internacional' },
  { value: 'Smartphone', label: 'Mobile' },
  { value: 'Zap', label: 'Automação' },
];

export function UseCasesTab({
  useCases,
  complementaryTemplates,
  availableTemplates = [],
  onChange,
  isDarkMode = false,
}: UseCasesTabProps) {
  // Theme-aware colors
  const colors = isDarkMode ? {
    sectionTitle: 'text-white',
    sectionDescription: 'text-gray-400',
    cardBg: 'bg-gray-800',
    cardBorder: 'border-gray-700',
    cardTitle: 'text-white',
    cardText: 'text-gray-300',
    buttonText: 'text-blue-400 hover:text-blue-300',
    buttonDanger: 'text-red-400 hover:text-red-300',
    dashedBorder: 'border-gray-600 hover:border-gray-500',
    dashedText: 'text-gray-400 hover:text-gray-300',
    inputBg: 'bg-gray-800',
    inputBorder: 'border-gray-600',
    inputText: 'text-white',
    badgeBg: 'bg-gray-700',
    badgeText: 'text-gray-300',
    emptyBg: 'bg-gray-800/50',
    emptyText: 'text-gray-400',
  } : {
    sectionTitle: 'text-gray-900',
    sectionDescription: 'text-gray-500',
    cardBg: 'bg-white',
    cardBorder: 'border-gray-200',
    cardTitle: 'text-gray-900',
    cardText: 'text-gray-600',
    buttonText: 'text-blue-600 hover:text-blue-800',
    buttonDanger: 'text-red-500 hover:text-red-700',
    dashedBorder: 'border-gray-300 hover:border-gray-400',
    dashedText: 'text-gray-500 hover:text-gray-600',
    inputBg: 'bg-white',
    inputBorder: 'border-gray-300',
    inputText: 'text-gray-900',
    badgeBg: 'bg-gray-100',
    badgeText: 'text-gray-700',
    emptyBg: 'bg-gray-50',
    emptyText: 'text-gray-500',
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<UseCase>({
    id: '',
    title: '',
    description: '',
    businessScenario: '',
    recommendedFor: [],
    icon: 'Lightbulb',
  });
  const [newRecommendedFor, setNewRecommendedFor] = useState('');

  const openAddModal = () => {
    setFormData({
      id: crypto.randomUUID(),
      title: '',
      description: '',
      businessScenario: '',
      recommendedFor: [],
      icon: 'Lightbulb',
    });
    setEditingIndex(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index: number) => {
    setFormData({ ...useCases[index] });
    setEditingIndex(index);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (!formData.title || !formData.description || !formData.businessScenario) {
      return;
    }

    const updated = [...useCases];
    if (editingIndex !== null) {
      updated[editingIndex] = formData;
    } else {
      updated.push(formData);
    }
    
    onChange('useCases', updated);
    setIsModalOpen(false);
  };

  const handleDelete = (index: number) => {
    onChange('useCases', useCases.filter((_, i) => i !== index));
  };

  const addRecommendedFor = () => {
    if (newRecommendedFor.trim() && !formData.recommendedFor.includes(newRecommendedFor.trim())) {
      setFormData({
        ...formData,
        recommendedFor: [...formData.recommendedFor, newRecommendedFor.trim()],
      });
      setNewRecommendedFor('');
    }
  };

  const removeRecommendedFor = (item: string) => {
    setFormData({
      ...formData,
      recommendedFor: formData.recommendedFor.filter(r => r !== item),
    });
  };

  // Complementary templates handlers
  const toggleComplementaryTemplate = (templateId: string) => {
    const updated = complementaryTemplates.includes(templateId)
      ? complementaryTemplates.filter(id => id !== templateId)
      : [...complementaryTemplates, templateId];
    onChange('complementaryTemplates', updated);
  };

  return (
    <div className="space-y-8">
      {/* Use Cases Section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Casos de Uso
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Descreva cenários de negócio onde este template é útil.
            </p>
          </div>
          <Button onClick={openAddModal} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Adicionar
          </Button>
        </div>

        {useCases.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Lightbulb className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum caso de uso cadastrado</p>
            <Button variant="ghost" onClick={openAddModal} className="mt-2">
              Adicionar primeiro caso de uso
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {useCases.map((useCase, index) => (
              <UseCaseCard
                key={useCase.id}
                useCase={useCase}
                onEdit={() => openEditModal(index)}
                onDelete={() => handleDelete(index)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Complementary Templates Section */}
      <section>
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Templates Complementares
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Selecione templates que funcionam bem em conjunto com este.
          </p>
        </div>

        {availableTemplates.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <p className="text-gray-500 text-sm">
              Nenhum outro template disponível para vincular.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {availableTemplates.map((template) => (
              <label
                key={template.id}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                  complementaryTemplates.includes(template.id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={complementaryTemplates.includes(template.id)}
                  onChange={() => toggleComplementaryTemplate(template.id)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{template.name}</p>
                  {template.description && (
                    <p className="text-xs text-gray-500 truncate">{template.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}
      </section>

      {/* Comparison Section */}
      <section>
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <GitCompare className="w-5 h-5" />
            Comparação com Similares
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Compare este template com alternativas similares.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <GitCompare className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            A comparação será gerada automaticamente com base nos templates da mesma categoria.
          </p>
        </div>
      </section>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingIndex !== null ? 'Editar Caso de Uso' : 'Novo Caso de Uso'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ícone
            </label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map((option) => {
                const Icon = ICON_MAP[option.value];
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: option.value })}
                    className={`p-2 rounded-lg border transition-colors ${
                      formData.icon === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-600'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                    title={option.label}
                  >
                    {Icon && <Icon className="w-5 h-5" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título *
            </label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Ex: Pagamentos Recorrentes"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva o caso de uso..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
          </div>

          {/* Business Scenario */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cenário de Negócio *
            </label>
            <textarea
              value={formData.businessScenario}
              onChange={(e) => setFormData({ ...formData, businessScenario: e.target.value })}
              placeholder="Descreva o cenário de negócio onde este caso de uso se aplica..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
          </div>

          {/* Recommended For */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recomendado Para
            </label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newRecommendedFor}
                onChange={(e) => setNewRecommendedFor(e.target.value)}
                placeholder="Ex: E-commerce, SaaS, Marketplace"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addRecommendedFor())}
              />
              <Button type="button" onClick={addRecommendedFor} variant="secondary">
                Adicionar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.recommendedFor.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={() => removeRecommendedFor(item)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              {editingIndex !== null ? 'Salvar' : 'Adicionar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default UseCasesTab;
